import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import {
  CLIENT_REPORT_BLOCKS,
  SEO_URLS_VOLUME_WARNING,
  defaultEnabledBlockIds,
  storageKeyForProject,
} from '../constants/clientReportBlocks';
import { blockAvailability, downloadClientReportJson } from '../utils/clientReportJson';

function loadStoredPrefs(slug) {
  try {
    const raw = localStorage.getItem(storageKeyForProject(slug));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveStoredPrefs(slug, data) {
  try {
    localStorage.setItem(storageKeyForProject(slug), JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

export default function ClientReportExportModal({
  open,
  onClose,
  slug,
  project,
  syntheseData,
  disabled,
  onSuccess,
  onError,
}) {
  const [enabled, setEnabled] = useState(() => new Set(defaultEnabledBlockIds()));
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  const km = syntheseData?.key_metrics || {};
  const auditsCount = km.nb_audits_seo ?? 0;

  useEffect(() => {
    if (!open || !slug) return;
    const stored = loadStoredPrefs(slug);
    if (stored?.enabledBlockIds?.length) {
      const merged = new Set(stored.enabledBlockIds);
      for (const block of CLIENT_REPORT_BLOCKS) {
        if (block.defaultEnabled) merged.add(block.id);
      }
      setEnabled(merged);
    } else {
      setEnabled(new Set(defaultEnabledBlockIds()));
    }
    setComment(stored?.consultantComment || '');
  }, [open, slug]);

  const persistPrefs = useCallback(() => {
    if (!slug) return;
    saveStoredPrefs(slug, {
      enabledBlockIds: [...enabled],
      consultantComment: comment,
    });
  }, [slug, enabled, comment]);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(persistPrefs, 400);
    return () => clearTimeout(t);
  }, [open, persistPrefs]);

  const blockStates = useMemo(
    () => CLIENT_REPORT_BLOCKS.map((block) => {
      const avail = blockAvailability(block.id, { project, syntheseData, auditsCount });
      const checked = enabled.has(block.id);
      const effective = block.required || (checked && avail.available);
      return { ...block, ...avail, checked: block.required ? true : checked, effective };
    }),
    [project, syntheseData, auditsCount, enabled],
  );

  const exportableCount = useMemo(
    () => blockStates.filter((b) => b.checked && (b.available || b.required)).length,
    [blockStates],
  );

  const canDownload = useMemo(
    () => blockStates.some((b) => b.id !== 'meta' && b.checked && b.available),
    [blockStates],
  );

  const showVolumeWarning = enabled.has('seoUrlsTable') && auditsCount > SEO_URLS_VOLUME_WARNING;

  const toggleBlock = (id, required) => {
    if (required) return;
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      const ids = [...enabled];
      if (!ids.includes('meta')) ids.unshift('meta');
      persistPrefs();
      await downloadClientReportJson(slug, project, {
        syntheseData,
        enabledBlockIds: ids,
        consultantComment: comment,
      });
      onSuccess?.();
      onClose();
    } catch (e) {
      onError?.(e?.message || 'Export impossible.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Exporter le rapport client (JSON)" size="lg">
      <p className="text-sm text-blue-gray-600 mb-4">
        Choisissez les blocs à inclure dans le fichier JSON (pour synthèse IA ou archivage).
      </p>

      <ul className="space-y-3 max-h-[42vh] overflow-y-auto pr-1">
        {blockStates.map((block) => (
          <li
            key={block.id}
            className={`rounded-lg border px-3 py-2.5 ${
              !block.available && !block.required
                ? 'border-neutral-100 bg-neutral-50 opacity-70'
                : 'border-neutral-200 bg-white'
            }`}
          >
            <label className={`flex gap-3 ${block.required ? 'cursor-default' : 'cursor-pointer'}`}>
              <input
                type="checkbox"
                className="mt-1 shrink-0"
                checked={block.checked}
                disabled={block.required || !block.available || loading}
                onChange={() => toggleBlock(block.id, block.required)}
              />
              <span className="min-w-0">
                <span className="text-sm font-medium text-blue-gray-900">{block.label}</span>
                <span className="block text-xs text-blue-gray-500 mt-0.5 leading-relaxed">
                  {block.description}
                </span>
                {!block.available && !block.required && (
                  <span className="block text-xs text-amber-700 mt-1">{block.reason}</span>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>

      <p className="text-xs text-blue-gray-500 mt-3">
        {exportableCount} bloc{exportableCount !== 1 ? 's' : ''} seront exportés.
      </p>
      {showVolumeWarning && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
          Export volumineux : {auditsCount} URLs dans le tableau SEO.
        </p>
      )}

      <div className="mt-4">
        <label htmlFor="client-report-comment" className="block text-sm font-medium text-blue-gray-800 mb-1">
          Vos observations (optionnel)
        </label>
        <textarea
          id="client-report-comment"
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          disabled={loading}
          placeholder="Points importants vus en dehors de l’audit, contexte client, priorités business…"
          className="w-full text-sm border border-neutral-200 rounded-lg px-3 py-2 text-blue-gray-800 placeholder:text-blue-gray-400 focus:outline-none focus:ring-2 focus:ring-accent/30"
        />
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button variant="secondary" type="button" onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={handleDownload}
          disabled={disabled || loading || !canDownload}
        >
          {loading ? 'Préparation…' : 'Télécharger le JSON'}
        </Button>
      </div>
    </Modal>
  );
}
